/**
 * Private R2 upload via Worker (multipart). Never uses a public bucket URL.
 */

import type { MediaObjectMeta, MediaUploadAdapter, MediaUploadResult } from "./uploadAdapter";
import { assertSameStorageKey, buildStorageKey } from "./storageKey";
import { apiFetch, getApiBase } from "../api/client";
import { MediaUploadError } from "./signTypes";

export function createApiUploadAdapter(): MediaUploadAdapter {
  return {
    id: "r2-private-api",

    async upload({ userId, asset, file }): Promise<MediaUploadResult> {
      const expectedKey = buildStorageKey({
        userId,
        kind: asset.kind,
        assetId: asset.id,
        fileName: asset.fileName,
        mimeType: asset.mimeType || file.type,
      });

      const form = new FormData();
      form.set("kind", asset.kind);
      form.set("assetId", asset.id);
      form.set("file", file, asset.fileName || "file");

      const result = await apiFetch<{
        ok?: boolean;
        id?: string;
        storageKey?: string;
        viewPath?: string;
        mimeType?: string;
        sizeBytes?: number;
        error?: string;
      }>("/api/media/upload", { method: "POST", body: form });

      if (!result.ok) {
        throw new MediaUploadError(result.error, "put", result.status || undefined);
      }

      const storageKey = result.data.storageKey || expectedKey;
      assertSameStorageKey(expectedKey, storageKey);

      // Same-origin private view path — not a public CDN URL.
      const viewPath = result.data.viewPath || "";
      const publicUrl = viewPath ? `${getApiBase()}${viewPath}` : "";

      const meta: MediaObjectMeta = {
        storageKey,
        userId,
        assetId: asset.id,
        kind: asset.kind,
        fileName: asset.fileName,
        mimeType: asset.mimeType || file.type,
        sizeBytes: file.size,
        createdAt: new Date().toISOString(),
      };

      return {
        storageKey,
        publicUrl,
        meta,
        via: "cloud",
      };
    },

    async getPreviewUrl(storageKey) {
      // Preview URLs are minted per media id by the API; storageKey alone is not enough.
      void storageKey;
      return null;
    },
  };
}
